import { useState, useEffect, createContext, useContext, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import CreateCampaignPage from "@/pages/CreateCampaignPage";
import CampaignDetailPage from "@/pages/CampaignDetailPage";
import { Sun, Moon } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);
// Theme Context
const ThemeContext = createContext(null);

export const useAuth = () => useContext(AuthContext);
export const useTheme = () => useContext(ThemeContext);
export { API };

function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => localStorage.getItem("aalign_theme") || "dark");

  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove("light", "dark");
    root.classList.add(theme);
    localStorage.setItem("aalign_theme", theme);
  }, [theme]);

  const toggleTheme = () => setTheme((t) => (t === "dark" ? "light" : "dark"));

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("aalign_token"));
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    localStorage.removeItem("aalign_token");
    setToken(null);
    setUser(null);
  }, []);

  useEffect(() => {
    if (token) {
      axios
        .get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then((res) => {
          setUser(res.data.user);
          setLoading(false);
        })
        .catch(() => {
          logout();
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [token, logout]);

  const login = (newToken, userData) => {
    localStorage.setItem("aalign_token", newToken);
    setToken(newToken);
    setUser(userData);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading, API }}>
      {children}
    </AuthContext.Provider>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex gap-2">
          <div className="ai-dot dot-bounce-1" />
          <div className="ai-dot dot-bounce-2" />
          <div className="ai-dot dot-bounce-3" />
        </div>
      </div>
    );
  }
  return user ? children : <Navigate to="/login" replace />;
}

function AppLayout() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Top Nav */}
      <nav className="sticky top-0 z-50 px-6 py-3 flex items-center justify-between bg-card/80 backdrop-blur-xl border-b border-border" data-testid="main-nav">
        <div className="flex items-center gap-8">
          <button
            onClick={() => navigate("/")}
            className="text-lg font-bold tracking-tight text-foreground hover:opacity-80 transition-opacity"
            data-testid="nav-logo"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            <span className="text-primary">a</span>align
            <span className="text-accent text-xs ml-1 font-normal" style={{ fontFamily: "IBM Plex Sans, sans-serif" }}>
              AI Campaign Builder
            </span>
          </button>
          <div className="hidden md:flex items-center gap-1">
            <button
              onClick={() => navigate("/")}
              className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors rounded-sm"
              data-testid="nav-dashboard"
            >
              Dashboard
            </button>
            <button
              onClick={() => navigate("/create")}
              className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors rounded-sm"
              data-testid="nav-create"
            >
              New Campaign
            </button>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground hidden sm:block">{user?.email}</span>
          <button
            onClick={toggleTheme}
            className="p-2 text-muted-foreground hover:text-foreground border border-border rounded-sm transition-colors"
            data-testid="theme-toggle-btn"
            title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
          >
            {theme === "dark" ? <Sun size={14} /> : <Moon size={14} />}
          </button>
          <button
            onClick={logout}
            className="text-xs text-muted-foreground hover:text-foreground px-3 py-1.5 border border-border rounded-sm transition-colors"
            data-testid="nav-logout"
          >
            Logout
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Routes>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/create" element={<CreateCampaignPage />} />
          <Route path="/campaign/:id" element={<CampaignDetailPage />} />
        </Routes>
      </main>
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <Toaster position="top-right" richColors />
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route
                path="/*"
                element={
                  <ProtectedRoute>
                    <AppLayout />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
